import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AppointmentForm from "./AppointmentForm";
import {
    Plus, MoreVertical, Calendar, Clock, User, Tag, Eye, EyeOff,
    Edit3, CheckCircle, XCircle, ChevronDown, ChevronRight, Navigation,
} from "lucide-react";
import { openDirections, resolveMapAddress } from "@/lib/directions";

const TABS = [
    { key: "upcoming",  label: "Upcoming" },
    { key: "completed", label: "Completed" },
    { key: "cancelled", label: "Cancelled" },
];

function fmtUKDate(iso) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}
function fmtTime(iso) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/**
 * Scheduled Visits section for the installer project page — 1:1 with mobile
 * ScheduledVisitsSection.tsx. Collapsible header, Upcoming/Completed/Cancelled
 * tabs with counts, per-visit action menu (edit / mark complete / cancel).
 *
 * DEV-003G.4 role scoping: when the signed-in user is an Engineer
 * (role=installer) and their engineer id is known, this section shows and
 * counts ONLY visits assigned to that engineer. Operations users see
 * every visit for the project. Customers keep their existing behaviour
 * (they read visits through the customer-facing journey view, not this
 * component).
 */
export default function ScheduledVisitsSection({
    projectId,
    onOpenMessages,
    userRole = null,
    myEngineerId = null,
    focusVisitId = null,
}) {
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    // UAT corrective pass · Engineer sections initialise as collapsed to
    // match the Operations "collapsed-by-default" convention. Users can
    // still expand/collapse via the header.
    const [expanded, setExpanded] = useState(false);
    const [tab, setTab] = useState("upcoming");
    const [formVisible, setFormVisible] = useState(false);
    const [editing, setEditing] = useState(null);
    const [menuFor, setMenuFor] = useState(null);
    const [confirmCancel, setConfirmCancel] = useState(null);
    // FIELD-001B2 · Highlight the visit that arrived via
    // `?visit=<id>` so the user can see we opened the right one.
    const [highlightedId, setHighlightedId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const list = await api.listAppointmentsAll(projectId);
            setVisits(list);
        } catch { /* best effort */ }
        setLoading(false);
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    // Engineer-scoped view: filter to visits assigned to the signed-in
    // engineer before deriving counts and lists. Operations users skip
    // this filter and see everything. If the engineer id hasn't resolved
    // yet, show nothing rather than leaking the wider crew's work — an
    // engineer without a resolved id shouldn't see other people's visits.
    const scopedVisits = useMemo(() => {
        if (userRole !== "installer") return visits;
        if (!myEngineerId) return [];
        return visits.filter((v) => v.engineer_id === myEngineerId);
    }, [visits, userRole, myEngineerId]);

    // FIELD-001B2 · Deep-link resolution. When a caller navigates with
    // `?visit=<id>#visits`, expand the section, switch to the matching
    // tab (or fall through when the visit was hidden by role scoping /
    // does not belong to the current project) and scroll the row into
    // view. Nothing else changes for regular navigation.
    useEffect(() => {
        if (!focusVisitId || loading) return;
        const target = scopedVisits.find((v) => v.id === focusVisitId);
        if (!target) return; // absent, cancelled from scope, or unauthorised — noop
        setExpanded(true);
        if (target.status === "completed") setTab("completed");
        else if (target.status === "cancelled") setTab("cancelled");
        else setTab("upcoming");
        setHighlightedId(target.id);
        const t = setTimeout(() => {
            const el = typeof document !== "undefined"
                ? document.querySelector(`[data-testid="visit-${target.id}"]`)
                : null;
            if (el && typeof el.scrollIntoView === "function") {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, 250);
        return () => clearTimeout(t);
    }, [focusVisitId, loading, scopedVisits]);

    const counts = useMemo(() => {
        const now = new Date();
        return {
            upcoming:  scopedVisits.filter((v) => v.status === "scheduled" && new Date(v.scheduled_at) >= now).length,
            completed: scopedVisits.filter((v) => v.status === "completed").length,
            cancelled: scopedVisits.filter((v) => v.status === "cancelled").length,
        };
    }, [scopedVisits]);

    const filtered = useMemo(() => {
        const now = new Date();
        if (tab === "upcoming") {
            return scopedVisits.filter((v) => v.status === "scheduled" && new Date(v.scheduled_at) >= now)
                         .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
        }
        if (tab === "completed") return scopedVisits.filter((v) => v.status === "completed").sort((a, b) => b.scheduled_at.localeCompare(a.scheduled_at));
        return scopedVisits.filter((v) => v.status === "cancelled").sort((a, b) => b.scheduled_at.localeCompare(a.scheduled_at));
    }, [scopedVisits, tab]);

    const openCreate = () => { setEditing(null); setFormVisible(true); };
    const openEdit = (v) => { setEditing(v); setMenuFor(null); setFormVisible(true); };

    const patch = async (v, patchBody, msg) => {
        try {
            const saved = await api.updateAppointment(v.id, patchBody);
            setVisits((prev) => prev.map((x) => x.id === saved.id ? { ...x, ...saved } : x));
            setMenuFor(null); setConfirmCancel(null);
            toast.success(msg);
        } catch { toast.error("Could not update visit"); }
    };

    return (
        <section className="mt-6" data-testid="visits-section">
            <div className="flex items-center justify-between mb-3">
                <button data-testid="visits-toggle" onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 text-[#131B2F] hover:text-[#3E9E2E] transition-colors">
                    {expanded ? <ChevronDown size={16} strokeWidth={1.75} /> : <ChevronRight size={16} strokeWidth={1.75} />}
                    <span className="text-lg font-medium">
                        Scheduled Visits{!expanded && ` (${counts.upcoming} upcoming)`}
                    </span>
                </button>
                <button data-testid="add-visit-btn" onClick={openCreate} className="pe-btn-primary text-sm h-9">
                    <Plus size={14} strokeWidth={2} /> New visit
                </button>
            </div>

            {expanded && (
                <>
                    <div className="flex gap-1 p-[3px] rounded-full border border-[#E5E7EB] bg-[#F5F5F7] self-start w-fit mb-3" data-testid="visits-tabs">
                        {TABS.map((t) => (
                            <button
                                key={t.key} data-testid={`visits-tab-${t.key}`}
                                onClick={() => setTab(t.key)}
                                className={`px-3.5 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${
                                    tab === t.key ? "bg-white text-[#131B2F] shadow-sm" : "text-[#4B5565] hover:text-[#131B2F]"
                                }`}
                            >
                                {t.label}
                                <span className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 ${
                                    tab === t.key ? "bg-[#3E9E2E] text-white" : "bg-[#E5E7EB] text-[#4B5565]"
                                }`}>{counts[t.key]}</span>
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="py-8 text-center text-sm text-[#4B5565]">Loading visits…</div>
                    ) : filtered.length === 0 ? (
                        <div className="py-8 text-center text-sm text-[#4B5565]">No {tab} visits.</div>
                    ) : (
                        <div className="space-y-2" data-testid="visits-list">
                            {filtered.map((v) => (
                                <div key={v.id}
                                     className={`pe-card p-4 transition-colors ${v.id === highlightedId ? "ring-2 ring-[#3E9E2E] bg-[#F1F8EC]" : ""}`}
                                     data-testid={`visit-${v.id}`}
                                     data-focused={v.id === highlightedId ? "true" : undefined}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <div className="text-base text-[#131B2F] font-medium truncate">{v.title}</div>
                                                {v.visibility === "internal_only" && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#E5E7EB] text-[#4B5565]">
                                                        <EyeOff size={10} /> Internal
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#4B5565]">
                                                <span className="inline-flex items-center gap-1.5"><Calendar size={12} strokeWidth={1.75} /> {fmtUKDate(v.scheduled_at)}</span>
                                                <span className="inline-flex items-center gap-1.5"><Clock size={12} strokeWidth={1.75} /> {v.arrival_window || fmtTime(v.scheduled_at)}</span>
                                                <span className="inline-flex items-center gap-1.5"><User size={12} strokeWidth={1.75} /> {v.engineer_name}</span>
                                                <span className="inline-flex items-center gap-1.5"><Tag size={12} strokeWidth={1.75} /> {v.appointment_type}</span>
                                                {(() => {
                                                    const addr = resolveMapAddress({ visit: v });
                                                    if (!addr) return null;
                                                    return (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); openDirections(addr); }}
                                                            data-testid={`visit-directions-${v.id}`}
                                                            title={`Directions to ${addr}`}
                                                            className="inline-flex items-center gap-1.5 text-sm text-[#131B2F] font-medium hover:underline"
                                                        >
                                                            <Navigation size={12} strokeWidth={2} /> Directions
                                                        </button>
                                                    );
                                                })()}
                                            </div>
                                            {v.customer_instructions && (
                                                <div className="mt-2 text-sm text-[#131B2F] bg-[#F1F8EC] border border-[#3E9E2E]/25 rounded-md p-2.5">
                                                    <span className="font-medium text-[#2E7E22]">Customer:</span> {v.customer_instructions}
                                                </div>
                                            )}
                                            {v.internal_notes && (
                                                <div className="mt-2 text-sm text-[#131B2F] bg-[#F5F5F7] border border-[#E5E7EB] rounded-md p-2.5">
                                                    <span className="font-medium text-[#4B5565]">Internal:</span> {v.internal_notes}
                                                </div>
                                            )}
                                        </div>
                                        <div className="relative">
                                            <button data-testid={`visit-menu-${v.id}`} onClick={() => setMenuFor(menuFor?.id === v.id ? null : v)} className="w-9 h-9 rounded-full flex items-center justify-center text-[#4B5565] hover:bg-[#F5F5F7]">
                                                <MoreVertical size={16} strokeWidth={1.75} />
                                            </button>
                                            {menuFor?.id === v.id && (
                                                <div className="absolute top-10 right-0 z-10 w-56 bg-white border border-[#E5E7EB] rounded-md shadow-lg py-1">
                                                    <MenuRow icon={Edit3} label="Edit visit"           onClick={() => openEdit(v)} testid={`visit-edit-${v.id}`} />
                                                    {v.status !== "completed" && (
                                                        <MenuRow icon={CheckCircle} label="Mark complete"
                                                            onClick={() => patch(v, { status: "completed" }, "Visit marked complete")} testid={`visit-complete-${v.id}`} />
                                                    )}
                                                    {v.status !== "cancelled" && (
                                                        <MenuRow icon={XCircle} label="Cancel visit" danger
                                                            onClick={() => { setMenuFor(null); setConfirmCancel(v); }} testid={`visit-cancel-${v.id}`} />
                                                    )}
                                                    {v.visibility === "customer_visible" ? (
                                                        <MenuRow icon={EyeOff} label="Make internal only"
                                                            onClick={() => patch(v, { visibility: "internal_only" }, "Visit set to internal only")} />
                                                    ) : (
                                                        <MenuRow icon={Eye} label="Notify customer"
                                                            onClick={() => patch(v, { visibility: "customer_visible" }, "Customer notified")} />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            <AppointmentForm
                open={formVisible}
                onClose={() => setFormVisible(false)}
                projectId={projectId}
                initial={editing}
                onSaved={(saved, msg) => {
                    setVisits((prev) => {
                        const exists = prev.find((v) => v.id === saved.id);
                        return exists ? prev.map((v) => v.id === saved.id ? { ...v, ...saved } : v) : [...prev, saved];
                    });
                    toast.success(msg);
                }}
            />

            {confirmCancel && (
                <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" data-testid="confirm-cancel">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
                        <div className="text-lg text-[#131B2F] font-medium">Cancel this visit?</div>
                        <div className="text-sm text-[#4B5565] mt-1">"{confirmCancel.title}" will move to Cancelled. The customer will see the change.</div>
                        <div className="mt-4 flex gap-2">
                            <button className="pe-btn-outline flex-1 justify-center h-11" onClick={() => setConfirmCancel(null)}>Keep</button>
                            <button data-testid="confirm-cancel-yes"
                                onClick={() => patch(confirmCancel, { status: "cancelled" }, "Visit cancelled")}
                                className="pe-btn-primary flex-1 justify-center h-11 !bg-[#E53E3E] hover:!bg-[#C53030]">
                                Cancel visit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

function MenuRow({ icon: Icon, label, onClick, danger, testid }) {
    return (
        <button data-testid={testid} onClick={onClick} className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${danger ? "text-[#E53E3E] hover:bg-[#FEE2E2]" : "text-[#131B2F] hover:bg-[#F5F5F7]"}`}>
            <Icon size={14} strokeWidth={1.75} /> {label}
        </button>
    );
}
